/**
 * Wake lock za način za avto (P2 §7.3).
 *
 * ⚠️ **Ponovni zajem ob `visibilitychange` ni podrobnost.** Sistem ključavnico
 * sam sprosti vsakič, ko zavihek odide v ozadje — tudi za hip, ob prejetem
 * klicu ali obvestilu. Brez ponovnega zajema se zaslon od takrat naprej ugaša
 * naprej in način za avto tiho neha delati natanko tisto, zaradi česar
 * obstaja.
 *
 * ⚠️ **Zahteva vidno stran.** Klic v ozadju vrže `NotAllowedError`; zato se
 * poskus zgodi šele, ko je stran spet vidna.
 *
 * Odpoved je tiha in prav je tako: podpora je ~93 % in tam, kjer je ni,
 * uporabnik ne more narediti ničesar. Sporočilo bi bilo o nečem, česar ni
 * zahteval in ne more popraviti (P4 §5).
 */

interface WakeLockLike {
  released: boolean;
  release(): Promise<void>;
}

interface WakeLockNavigator {
  wakeLock?: { request(type: 'screen'): Promise<WakeLockLike> };
}

export interface WakeLockHandle {
  /** Sprosti ključavnico in neha poslušati vidnost. */
  release(): void;
}

export function keepScreenAwake(): WakeLockHandle {
  const api = (navigator as WakeLockNavigator).wakeLock;
  let sentinel: WakeLockLike | null = null;
  let done = false;

  function acquire(): void {
    if (done || api === undefined) return;
    if (document.visibilityState !== 'visible') return;
    void api
      .request('screen')
      .then((lock) => {
        // Med čakanjem na obljubo je lahko `release()` že padel.
        if (done) {
          void lock.release().catch(() => {});
          return;
        }
        sentinel = lock;
      })
      .catch(() => {});
  }

  function onVisibility(): void {
    if (document.visibilityState !== 'visible') return;
    if (sentinel !== null && !sentinel.released) return;
    acquire();
  }

  document.addEventListener('visibilitychange', onVisibility);
  acquire();

  return {
    release() {
      done = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const lock = sentinel;
      sentinel = null;
      if (lock !== null) void lock.release().catch(() => {});
    },
  };
}
